import { gql, useApolloClient } from '@apollo/client';
import { Fragment, useContext, useEffect, useState } from 'react';
import { ApplicationContext } from 'shared';
import { Button, Col, Divider, Form, Grid, Input, Message, Panel, PanelGroup, Row, toaster } from 'rsuite';
import { FORMAT, setHeaderTitle } from 'lib/env';
import { isMobile } from 'react-device-detect';
import orderBy from 'lodash/orderBy';
import { format, parseISO } from 'date-fns';
import { toTimeZone } from 'lib/date';
import { useGetToken } from 'lib/auth';
import { getAllFields } from 'lib/helpers/field';

const PortalWorkOrderView = () => {
  const client = useApolloClient();
  const { showError } = useContext(ApplicationContext);
  const [workorder, setWorkOrder] = useState<any>(null);
  const tokenIsExpired = false; //(token as any).exp < new Date().getTime() / 1000;
  const token: any = useGetToken(true);
  const [fields, setFields] = useState<any>([]);
  const [comments, setComments] = useState<string>('');

  useEffect(() => {
    (async function getFieldsData() {
      const result = await getAllFields(client);
      setFields(result);
    })();
  }, []);

  useEffect(() => {
    setHeaderTitle('Work Order');

    (async () => {
      // if (!tokenIsExpired) {
      const data = await client.query({
        query: gql`
          query workOrder($guid: ID) {
            workOrder(guid: $guid) {
              id
              guid
              applicationGroup
              workType
              customerComments
              jobs {
                guid
                startDate
                endDate
                timeOnSite
                timeFromSite
                props
                status
                photos {
                  guid
                  photoBefore
                  photoAfter
                  descriptionBefore
                  descriptionAfter
                }
                weather {
                  weather
                }
              }
              customer {
                id
                displayName
                displayAddress
                email
              }
            }
          }
        `,
        variables: {
          guid: (token as any).data.resource
        }
      });

      setWorkOrder(data.data.workOrder);
      setComments(data.data.workOrder.customerComments)
    })();
  }, []);

  const handleUpdateComments = async (guid: string, comments: string) => {
    try {
      const response: any = await client.mutate({
        mutation: gql`
          mutation publicUpdateWorkOrderCustomerComments($guid: ID!, $comments: String!) {
            publicUpdateWorkOrderCustomerComments(
              guid: $guid
              comments: $comments
            ) {
              success
              code
              message
            }
          }
        `,
        variables: { guid, comments }
      });

      toaster.push(
        <Message type={response.data.publicUpdateWorkOrderCustomerComments.success ? 'success' : 'error'} showIcon closable>{response.data.publicUpdateWorkOrderCustomerComments.message}</Message>
      );
    } catch (err) {
      showError(err);
    }
  };

  const ColoredStatus = ({ job }: any) => {
    let status = <span className="text-danger">Not Completed</span>
    if (job.status === 'completed') {
      status = <span className="text-success">Completed</span>
    } else if (job.status === 'canceled') {
      status = <span className="text-warning">Canceled</span>
    }

    return <div style={{ fontSize: '14px' }}><span>{format(parseISO(job.startDate), FORMAT.DAY_MONTH_DATE)}</span> - {status}</div>;
  }

  return (
    <div className="App">
      {tokenIsExpired
        ? <Fragment>
          <Grid>
            <Row>
              <Col xs={24}>
                <br />
                <Message type="error">This link has expired, please contact our office to receive a new link.</Message>
              </Col>
            </Row>
          </Grid>
        </Fragment>
        : <Fragment>
          {workorder &&
            <Grid fluid={isMobile}>
              <Row>
                <Col xs={14} md={12}>
                  <h1>{workorder.customer.displayName}</h1>
                  <p><strong>{workorder.customer.displayAddress}</strong></p>
                  <p>{fields.find((f: any) => f.key === workorder.workType)?.title}</p>
                  <br />
                </Col>
                <Col xs={10} md={12} style={{ textAlign: 'right' }}>
                  <h1>{fields.find((f: any) => f.key === workorder.applicationGroup)?.title}</h1>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <div className='mb-8'>
                    <label>Comments:</label>
                    <Input as={'textarea'} value={comments} onChange={setComments} />
                    <Form.HelpText>
                      Provide access codes or any specific information related to your service.
                      Please note, this information is available only to the site crew.
                    </Form.HelpText>
                  </div>
                  <Button appearance='primary' size="sm" onClick={() => handleUpdateComments(workorder.guid, comments)}>Save</Button>
                </Col>
              </Row>
              <br />
              <Row>
                <Col xs={24}>
                  <label>Jobs:</label>

                  <PanelGroup accordion defaultActiveKey={1} bordered>
                    {orderBy(workorder.jobs, 'startDate', 'desc').map((j: any) =>
                      <Panel
                        key={j.guid}
                        header={<ColoredStatus job={j} />} eventKey={j.guid}
                      >
                        <Grid fluid className="p-0">
                          <Row>
                            {j.weather.length > 0 &&
                              <Col xs={5} md={2}>
                                <img src={j.weather[0].weather.find((k: any) => k.key === 'icon').value} />
                              </Col>
                            }
                            {j.weather.length > 0 &&
                              <Col xs={9} md={4}>
                                Low: {j.weather[0].weather.find((k: any) => k.key === 'low').value}<br />
                                High: {j.weather[0].weather.find((k: any) => k.key === 'high').value}<br />
                                Precipitation: {j.weather[0].weather.find((k: any) => k.key === 'precipitation').value}
                              </Col>
                            }
                            <Col xs={10} md={5}>
                              Time on Site: {j.timeOnSite ? format(toTimeZone(parseISO(j.timeOnSite), 'America/Edmonton'), FORMAT.TIME24) : <span className="text-mutted">Not available</span>}<br />
                              Time off Site: {j.timeFromSite ? format(toTimeZone(parseISO(j.timeFromSite), 'America/Edmonton'), FORMAT.TIME24) : <span className="text-mutted">Not available</span>}<br />
                              Deicer User: {j.props.de_icer ? j.props.de_icer + 'kg' : <span className="text-mutted">-</span>}<br />
                            </Col>
                          </Row>
                        </Grid>


                        {j.photos.length > 0 &&
                          <Fragment>
                            <Divider />
                            <Grid fluid className="p-0">
                              {j.photos.map((p: any) =>
                                <Row key={p.guid}>
                                  <Col xs={12}>
                                    Before
                                    {p.descriptionBefore && <span> ({p.descriptionBefore})</span>}:
                                    {p.photoBefore
                                      ? <img style={{ width: '100%', height: 'auto' }} src={p.photoBefore} />
                                      : <p className="text-mutted">Not available</p>
                                    }
                                  </Col>
                                  <Col xs={12}>
                                    After
                                    {p.descriptionAfter && <span> ({p.descriptionAfter})</span>}:
                                    {p.photoAfter
                                      ? <img style={{ width: '100%', height: 'auto' }} src={p.photoAfter} />
                                      : <p className="text-mutted">Not available</p>
                                    }
                                  </Col>
                                </Row>
                              )}
                            </Grid>
                          </Fragment>
                        }
                      </Panel>
                    )}
                  </PanelGroup>
                </Col>
              </Row>
            </Grid>
          }
        </Fragment>
      }
    </div>
  );
}

export default PortalWorkOrderView;
